import { useState, type FormEvent } from 'react';
import { Button, Dialog, Input, Label, Select } from '../components/ui';
import { useCrews } from '../lib/queries';
import { useAdvanceStage, useScheduleStage, useWaterfallSchedule, type CascadeAnchor } from '../lib/mutations';
import { CASCADE_ANCHORS } from '../lib/boardStatus';
import { toInputDate, todayISO } from '../lib/utils';
import type { StageStatus } from '../lib/database.types';
import { STATUS_LABELS } from '../lib/database.types';

interface ScheduleStageDialogProps {
    open: boolean;
    onClose: () => void;
    jobId: string;
    jobStageId: string;
    stageName: string;
    jobLabel: string;
    currentDate: string | null;
    currentCrewId: string | null;
}

/**
 * Which columns a cascade from this stage will fill, named as they appear on the
 * board. Plumbing is set in the data but deliberately not listed, because the
 * column is hidden and naming something Andrea cannot see would read as a bug.
 *
 * Every stage except Pour can anchor now. Pour is last, so it has nothing to
 * fill and never offers the option.
 */
const CASCADE_FILLS: Record<string, string> = {
    Pin: 'Box, Plumbing Inspection, Prep, Slab Inspection and Pour',
    Box: 'Plumbing Inspection, Prep, Slab Inspection and Pour',
    'Plumbing Inspection': 'Prep, Slab Inspection and Pour',
    Prep: 'Slab Inspection and Pour',
    'Slab Inspection': 'Pour',
};

export function ScheduleStageDialog({
    open,
    onClose,
    jobId,
    jobStageId,
    stageName,
    jobLabel,
    currentDate,
    currentCrewId,
}: ScheduleStageDialogProps) {
    const { data: crews = [] } = useCrews();
    const scheduleMut = useScheduleStage();
    const advanceMut = useAdvanceStage();
    const waterfallMut = useWaterfallSchedule();

    // Every stage except Pour cascades now. When a date slips mid-job, the
    // stages after it should follow without Andrea re-entering each one.
    const canCascade = CASCADE_ANCHORS.has(stageName);

    const [scheduledDate, setScheduledDate] = useState(toInputDate(currentDate));
    const [crewId, setCrewId] = useState(currentCrewId ?? '');
    const [statusChange, setStatusChange] = useState<StageStatus | ''>('');
    const [completionDate, setCompletionDate] = useState(todayISO());
    const [notes, setNotes] = useState('');
    const [waterfall, setWaterfall] = useState(true);
    const [error, setError] = useState<string | null>(null);

    async function handleSave(e: FormEvent) {
        e.preventDefault();
        setError(null);

        try {
            const dateChanged = scheduledDate !== toInputDate(currentDate);
            const crewChanged = (crewId || null) !== currentCrewId;

            // Cascade sets this stage's own date as well, so the single
            // schedule_stage call is redundant on that path.
            if (canCascade && waterfall && scheduledDate) {
                await waterfallMut.mutateAsync({
                    p_job_id: jobId,
                    p_from_stage: stageName as CascadeAnchor,
                    p_date: scheduledDate,
                });
                if (crewChanged) {
                    await scheduleMut.mutateAsync({
                        p_job_stage_id: jobStageId,
                        p_scheduled_date: scheduledDate,
                        p_crew_id: crewId || null,
                    });
                }
            } else if (scheduledDate && (dateChanged || crewChanged)) {
                await scheduleMut.mutateAsync({
                    p_job_stage_id: jobStageId,
                    p_scheduled_date: scheduledDate,
                    p_crew_id: crewId || null,
                });
            }

            // If status change requested, call field_advance_stage
            if (statusChange) {
                await advanceMut.mutateAsync({
                    p_job_stage_id: jobStageId,
                    p_new_status: statusChange,
                    p_completion_date: statusChange === 'complete' ? completionDate : null,
                    p_notes: notes || null,
                });
            }

            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Save failed');
        }
    }

    const busy = scheduleMut.isPending || advanceMut.isPending || waterfallMut.isPending;
    const canSubmit = scheduledDate || statusChange;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={stageName}
            subtitle={jobLabel}
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={!canSubmit || busy}
                        type="submit"
                        form="schedule-form"
                    >
                        {busy ? 'Saving...' : 'Save'}
                    </Button>
                </>
            }
        >
            <form id="schedule-form" onSubmit={handleSave} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label htmlFor="scheduled-date">Scheduled date</Label>
                        <Input
                            id="scheduled-date"
                            type="date"
                            value={scheduledDate}
                            onChange={(e) => setScheduledDate(e.target.value)}
                        />
                    </div>
                    <div>
                        <Label htmlFor="crew">Crew</Label>
                        <Select
                            id="crew"
                            value={crewId}
                            onChange={(e) => setCrewId(e.target.value)}
                        >
                            <option value="">— none —</option>
                            {crews.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </Select>
                    </div>
                </div>

                {canCascade ? (
                    <label className="flex items-start gap-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={waterfall}
                            onChange={(e) => setWaterfall(e.target.checked)}
                            className="accent-signal-500 mt-0.5"
                        />
                        <span className="text-sm text-graphite-800 dark:text-graphite-200">
                            Fill the rest of the schedule from this date
                            <span className="block text-xs text-graphite-500">
                                Sets {CASCADE_FILLS[stageName]} using working days, so nothing lands on
                                a weekend. Stages already in progress or complete are left alone.
                            </span>
                        </span>
                    </label>
                ) : null}

                <div className="pt-3 border-t border-graphite-200 dark:border-graphite-800">
                    <Label htmlFor="status-change">Status change (optional)</Label>
                    <Select
                        id="status-change"
                        value={statusChange}
                        onChange={(e) => setStatusChange(e.target.value as StageStatus | '')}
                    >
                        <option value="">— leave unchanged —</option>
                        <option value="scheduled">{STATUS_LABELS.scheduled}</option>
                        <option value="in_progress">{STATUS_LABELS.in_progress}</option>
                        <option value="complete">{STATUS_LABELS.complete}</option>
                        <option value="on_hold">{STATUS_LABELS.on_hold}</option>
                        <option value="pending">{STATUS_LABELS.pending}</option>
                    </Select>
                </div>

                {statusChange === 'complete' ? (
                    <div>
                        <Label htmlFor="completion-date">Completion date</Label>
                        <Input
                            id="completion-date"
                            type="date"
                            value={completionDate}
                            onChange={(e) => setCompletionDate(e.target.value)}
                            required
                        />
                    </div>
                ) : null}

                {statusChange ? (
                    <div>
                        <Label htmlFor="notes">Notes (optional)</Label>
                        <Input
                            id="notes"
                            type="text"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Weather, crew change, delay..."
                        />
                    </div>
                ) : null}

                {error ? (
                    <div className="border-l-2 border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-amber-600 dark:text-amber-400">
                        {error}
                    </div>
                ) : null}
            </form>
        </Dialog>
    );
}
